/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { InfiniteData } from "@reduxjs/toolkit/query"
import {
  FoodmaxapipbListSellingTimesResponse,
  FoodmaxapipbGetModifierGroupDetailsByModifierGroupIDsResponse,
  FoodmaxapipbGetItemDetailsByItemIDsResponse,
  FoodmaxapipbSearchItemsResponse,
  FoodmaxapipbSearchMenusForMexResponse,
  FoodmaxapipbMartItemMenuDisplayV2Response,
  FoodmaxapipbListAttributesResponse,
  FoodmaxapipbUpdateItemAvailableStatusV2Response,
  FoodmaxapipbCreateMenuGroupResponse,
  FoodmaxapipbUpsertItemV2Response,
  FoodmaxapipbMenuGroupUpsertItemV2Response,
  FoodmaxapipbGetTranslationResponse,
  FoodmaxapipbListItemClassesResponse,
  FoodmaxapipbPublishMenuGroupResponse,
} from "generated/foodmaxapi.type"
import { FetchStoreListResponse } from "types/api/menuFetchStoreList.type"
import { FetchMerchantDetailResponse } from "types/api/menuFetchStoreDetail.type"
import { FetchItemsWithoutPhotoCountResponse } from "types/api/menuFetchItemsWithoutPhotoCount.type"
import { FetchSupportedAttributeClusterResponse } from "types/api/menuFetchAttributeOptions.type"
import {
  FetchMerchantAggregateResponse,
  FetchMenuGroupListResponse,
} from "types/api"
import { FetchTranslationResponse } from "types/api/menuFetchTranslation.type"
import { getDalErrMessage, parseJSONSafely } from "utils/misc"
import { createRequestHeaders } from "./apiClient"
import {
  CACHE_QUERY_TAGS,
  PARTIAL_MENU_REFRESH_TAGS,
  endpoints,
  dpApiEndpoints,
} from "./constants"
import {
  ListSellingTimesRequest,
  GetModifierGroupsByIDsRequest,
  GetItemDetailsByItemIDsRequest,
  SearchItemsRequest,
  SearchMenusForMexRequest,
  MartItemMenuDisplayV2Request,
  UploadFileRequest,
  UploadFileResponse,
  ListAttributesRequest,
  DeleteItemRequest,
  DeleteItemV2Request,
  DeleteCategoryRequest,
  DeleteMenuGroupRequest,
  UpdateItemsAvailableV1Request,
  UpdateItemsSellingTimeRequest,
  SearchOutletsRequest,
  FetchMerchantDetailRequest,
  FetchMerchantAggregateRequest,
  UpdateExistingTranslationRequest,
  FetchMenuGroupListRequest,
  ResetMenuGroupRequest,
  HttpMethod,
  CreateMenuGroupRequest,
  UpsertItemV2RtkRequest,
  GetTranslationRequest,
  FetchDepartmentsForGrabCategoryRequest,
  FetchSubdepartmentsForGrabCategoryRequest,
  GetVerifyTranslationRequest,
  PublishMenuGroupRequest,
  SortDepartmentsRequest,
  SortCategoriesRequest,
  SortItemsRequest,
  SortDepartmentItemsRequest,
  PostFeedbackGrabCategoryRequest,
  FetchSupportedAttributeClusterRequest,
} from "./types"
import { foodMerchantBaseApi, dpApiBaseApi, portalBaseApi } from "./baseApis"

// Maximum number of itemIDs that can be sent in a single GetItemDetailsByItemIDs request
export const GET_ITEM_DETAILS_BATCH_SIZE = 100

// Default page size for list attributes
export const DEFAULT_PAGE_SIZE_LIST_ATTRIBUTES = 100

export const menuApi = foodMerchantBaseApi.injectEndpoints({
  endpoints: builder => ({
    listSellingTimes: builder.query<
      FoodmaxapipbListSellingTimesResponse,
      ListSellingTimesRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        isActive = true,
        page,
        pageSize,
        name,
      }) => ({
        url: endpoints.listSellingTimes,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          merchantID,
          isActive,
          page,
          pageSize,
          name,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.listSellingTimes],
    }),
    getModifierGroupsByIDs: builder.query<
      FoodmaxapipbGetModifierGroupDetailsByModifierGroupIDsResponse,
      GetModifierGroupsByIDsRequest
    >({
      query: ({ merchantID, merchantGroupID, modifierGroupIDs }) => ({
        url: endpoints.modifierGroupsDetails,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          modifierGroupIDs,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.menuV3ModifierGroups],
    }),
    getItemDetailsByItemIDs: builder.infiniteQuery<
      FoodmaxapipbGetItemDetailsByItemIDsResponse,
      GetItemDetailsByItemIDsRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (
          _lastPage,
          _allPages,
          lastPageParam,
          _allPageParams,
          queryArg,
        ) => {
          // Calculate  items loaded after this page
          const expectedLoadedCount =
            (lastPageParam + 1) * GET_ITEM_DETAILS_BATCH_SIZE
          const totalItemIDs = queryArg.itemIDs?.length || 0

          // Continue fetching if we haven't reached all itemIDs yet
          return expectedLoadedCount < totalItemIDs
            ? lastPageParam + 1
            : undefined
        },
      },
      query: ({ queryArg, pageParam }) => {
        const itemIDs = queryArg.itemIDs || []
        const batchIndex = pageParam as number
        const startIndex = batchIndex * GET_ITEM_DETAILS_BATCH_SIZE
        const endIndex = startIndex + GET_ITEM_DETAILS_BATCH_SIZE
        const itemIDsBatch = itemIDs.slice(startIndex, endIndex)

        return {
          url: endpoints.itemDetailsByItemIDs,
          headers: createRequestHeaders({
            merchantID: queryArg.merchantID,
            merchantGroupID: queryArg.merchantGroupID,
          }),
          params: {
            itemIDs: itemIDsBatch,
            needStock: queryArg.needStock,
            needModifierGroups: queryArg.needModifierGroups,
          },
        }
      },
      providesTags: [CACHE_QUERY_TAGS.menuV3Items],
    }),
    searchItems: builder.query<
      FoodmaxapipbSearchItemsResponse,
      SearchItemsRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        searchKey,
        needClassItemsInfo = true,
        needCategoryInfo = true,
        needStock = true,
        needModifierGroups = false,
      }) => ({
        url: endpoints.searchItems,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          searchKey,
          needClassItemsInfo,
          needCategoryInfo,
          needStock,
          needModifierGroups,
        },
      }),
    }),
    /**
     * Search menus for merchant
     * GET /v2/search-menu
     */
    searchMenusForMex: builder.query<
      FoodmaxapipbSearchMenusForMexResponse,
      SearchMenusForMexRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        keyword,
        entityType,
        departmentID,
      }) => ({
        url: endpoints.searchMenusForMex,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          keyword,
          entityType,
          departmentID,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.searchMenusForMex],
    }),
    /**
     * Fetch catalogue (mart-menu)
     * GET /v2/mart-menu
     */
    martItemMenuDisplayV2: builder.query<
      FoodmaxapipbMartItemMenuDisplayV2Response,
      MartItemMenuDisplayV2Request
    >({
      query: ({ merchantID, merchantGroupID, hideModifierGroup = true }) => ({
        url: endpoints.martItemMenuDisplayV2,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          hideModifierGroup,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.martItemMenuDisplayV2],
      keepUnusedDataFor: Infinity,
    }),
    /**
     * Upload a product/item photo
     * POST /v2/upload-file
     * Returns the CDN URL of the uploaded image.
     */
    uploadFile: builder.mutation<UploadFileResponse, UploadFileRequest>({
      query: ({ merchantID, file, category }) => ({
        url: endpoints.uploadFile,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID }),
        data: { file, category },
      }),
    }),
    /**
     * List attributes
     * GET /v1/attributes (merchant) or /v2/menu-groups/attributes (menu group)
     */
    listAttributes: builder.query<
      FoodmaxapipbListAttributesResponse,
      ListAttributesRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        isMenuGroup,
        attributeClusterIDs,
        page,
        name,
        locale,
      }) => ({
        url: isMenuGroup
          ? endpoints.listMenuGroupAttributes
          : endpoints.listAttributes,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          attributeClusterIDs,
          name,
          locale,
          page,
          pageSize: DEFAULT_PAGE_SIZE_LIST_ATTRIBUTES,
          status: "Enabled",
        },
      }),
    }),
    /**
     * Sort categories within a section.
     * PUT /categories-sort or /v1/menu-groups/categories-sort
     */
    sortCategories: builder.mutation<void, SortCategoriesRequest>({
      query: ({
        merchantID,
        merchantGroupID,
        sectionID,
        sorts,
        menuGroupID,
        showError = true,
        showSuccess = false,
        successOptions,
      }) => ({
        url: menuGroupID
          ? endpoints.sortCategoriesMenuGroup
          : endpoints.sortCategories,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          sectionSorts: [{ sectionID, sorts }],
          menuGroupID,
        },
        extraOptions: { showError, showSuccess, successOptions },
      }),
    }),
    /**
     * Fetch list of menu groups for the merchant group.
     * GET /v1/menu-groups
     */
    fetchMenuGroupList: builder.query<
      FetchMenuGroupListResponse,
      FetchMenuGroupListRequest
    >({
      query: ({ merchantGroupID, menuGroupName, isWithItemPhotoCount }) => ({
        url: endpoints.fetchMenuGroupList,
        headers: createRequestHeaders({ merchantGroupID }),
        // Match legacy Redux URL: omit menuGroupName when falsy (axios keeps "")
        params: {
          menuGroupName: menuGroupName || undefined,
          isWithItemPhotoCount: isWithItemPhotoCount ?? false,
        },
        data: {
          menuGroupName,
        },
        extraOptions: { showGlobalLoading: true },
      }),
    }),
    /**
     * bulk update item availability
     * PUT /v1/items/available-status
     */
    updateItemsAvailableV1: builder.mutation<
      FoodmaxapipbUpdateItemAvailableStatusV2Response,
      UpdateItemsAvailableV1Request
    >({
      query: ({
        merchantID,
        merchantGroupID,
        availableStatus,
        itemIDs,
        departmentIDs,
        subDepartmentIDs,
        excludedSubDepartmentIDs,
        excludedItemIDs,
      }) => ({
        url: endpoints.updateItemsAvailableV1,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          itemIDs,
          availableStatus,
          departmentIDs,
          subDepartmentIDs,
          excludedSubDepartmentIDs,
          excludedItemIDs,
        },
        extraOptions: {
          showGlobalLoading: true,
          showError: true,
        },
      }),
      invalidatesTags: PARTIAL_MENU_REFRESH_TAGS,
    }),
    /**
     * Bulk update items selling time
     * PUT /v2/items/sellingTime
     */
    updateItemsSellingTime: builder.mutation<
      void,
      UpdateItemsSellingTimeRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        itemIDs,
        departmentIDs,
        subDepartmentIDs,
        excludedSubDepartmentIDs,
        excludedItemIDs,
        sellingTimeID,
      }) => ({
        url: endpoints.updateItemsSellingTimeV2,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          itemIDs,
          departmentIDs,
          subDepartmentIDs,
          excludedSubDepartmentIDs,
          excludedItemIDs,
          item: { sellingTimeID },
        },
      }),
      invalidatesTags: PARTIAL_MENU_REFRESH_TAGS,
    }),
    /**
     * Sort departments or sub-departments.
     * PUT /departments-sort or /v1/menu-groups/departments-sort
     */
    sortDepartments: builder.mutation<void, SortDepartmentsRequest>({
      query: ({
        merchantID,
        merchantGroupID,
        sorts,
        parentDepartmentID,
        menuGroupID,
        showError = true,
        showSuccess = false,
        successOptions,
      }) => ({
        url: menuGroupID
          ? endpoints.sortDepartmentsMenuGroup
          : endpoints.sortDepartments,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          parentDepartmentID,
          sorts,
          menuGroupID,
        },
        extraOptions: { showError, showSuccess, successOptions },
      }),
      invalidatesTags: [CACHE_QUERY_TAGS.menuV3Hierarchies],
    }),
    /**
     * Sort items within a category.
     * PUT /items-sort or /v1/menu-groups/items-sort
     */
    sortItems: builder.mutation<void, SortItemsRequest>({
      query: ({
        merchantID,
        merchantGroupID,
        categoryID,
        sorts,
        menuGroupID,
        showError = true,
        showSuccess = false,
        successOptions,
      }) => ({
        url: menuGroupID ? endpoints.sortItemsMenuGroup : endpoints.sortItems,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          categoryID,
          sorts,
          menuGroupID,
        },
        extraOptions: { showError, showSuccess, successOptions },
      }),
      invalidatesTags: [
        CACHE_QUERY_TAGS.menuV3Items,
        CACHE_QUERY_TAGS.menuV3Hierarchies,
      ],
    }),
    /**
     * Delete a category
     * DELETE /v2/categories/:categoryID or /v1/menu-groups/categories/:categoryID
     */
    deleteCategory: builder.mutation<void, DeleteCategoryRequest>({
      query: ({
        merchantID,
        merchantGroupID,
        categoryID,
        menuGroupID,
        showGlobalLoading,
        showSuccess,
        successOptions,
        showError,
      }) => ({
        url: menuGroupID
          ? `${endpoints.deleteCategoryMenuGroup}/${categoryID}`
          : `${endpoints.deleteCategoryV2}/${categoryID}`,
        method: HttpMethod.DELETE,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          menuGroupID,
          categoryID,
        },
        extraOptions: {
          showError: showError ?? true,
          showGlobalLoading,
          showSuccess,
          successOptions,
        },
      }),
      invalidatesTags: PARTIAL_MENU_REFRESH_TAGS,
    }),
    /**
     * Delete a menu group
     * DELETE /v1/menu-groups/:menuGroupID/delete
     */
    deleteMenuGroup: builder.mutation<void, DeleteMenuGroupRequest>({
      query: ({ merchantGroupID, menuGroupID }) => ({
        url: endpoints.deleteMenuGroup(menuGroupID),
        method: HttpMethod.DELETE,
        headers: createRequestHeaders({ merchantGroupID }),
        data: { menuGroupID },
        extraOptions: { showGlobalLoading: true, showSuccess: true },
      }),
    }),
    /**
     * Create a new menu group
     * POST /v1/menu-groups
     */
    createMenuGroup: builder.mutation<
      FoodmaxapipbCreateMenuGroupResponse,
      CreateMenuGroupRequest
    >({
      query: ({ merchantGroupID, menuGroup, menuGroupID, linkedStoreIDs }) => ({
        url: endpoints.createMenuGroup,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantGroupID }),
        data: {
          menuGroup,
          menuGroupID,
          linkedStoreIDs,
        },
        extraOptions: {
          showError: true,
          getErrorNotificationContent: ({ data, defaultMessage }) => {
            const message =
              (data as { message?: string })?.message || defaultMessage

            // Check for DAL error message
            const dalErrMsg = getDalErrMessage(message, true)
            if (dalErrMsg) {
              return { message: dalErrMsg }
            }

            // Parse JSON and check for merchantGroup error
            const dataAsObj = parseJSONSafely<{
              merchantGroup?: string
            }>(message)
            if (dataAsObj?.merchantGroup) {
              return { message: dataAsObj.merchantGroup }
            }

            return { message: defaultMessage }
          },
        },
      }),
    }),
    /**
     * Upsert (create or update) an item using the V2 endpoint.
     * POST /v2/upsert-item (store) or /v2/menu-groups/upsert-item (menu group)
     */
    upsertItemV2: builder.mutation<
      | FoodmaxapipbUpsertItemV2Response
      | FoodmaxapipbMenuGroupUpsertItemV2Response,
      UpsertItemV2RtkRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        menuGroupID,
        item,
        categoryID,
        itemAttributeValues,
        getErrorNotificationContent,
      }) => ({
        url: menuGroupID
          ? endpoints.upsertItemV2MenuGroup
          : endpoints.upsertItemV2,
        method: HttpMethod.POST,
        headers: createRequestHeaders({
          merchantID: merchantID || menuGroupID,
          merchantGroupID,
        }),
        data: {
          item,
          categoryID,
          itemAttributeValues,
          ...(menuGroupID && { menuGroupID }),
        },
        extraOptions: {
          showError: true,
          getErrorNotificationContent,
        },
      }),
      invalidatesTags: PARTIAL_MENU_REFRESH_TAGS,
    }),
    /**
     * delete a single item
     * DELETE /v2/items/:itemID  (no menuGroupID)
     * DELETE /v1/menu-groups/items/:itemID  (with menuGroupID)
     */
    deleteItem: builder.mutation<void, DeleteItemRequest>({
      query: ({
        merchantID,
        merchantGroupID,
        itemID,
        menuGroupID,
        showGlobalLoading,
        getErrorNotificationContent,
      }) => ({
        url: menuGroupID
          ? `/v1/menu-groups/items/${itemID}`
          : `${endpoints.deleteItem}/${itemID}`,
        method: HttpMethod.DELETE,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          itemID,
          menuGroupID,
        },
        extraOptions: {
          showError: true,
          showGlobalLoading,
          getErrorNotificationContent,
        },
      }),
      invalidatesTags: PARTIAL_MENU_REFRESH_TAGS,
    }),
    /**
     * Get menu translation
     * POST /v1/menu-translations or /v1/menu-groups/menu-translations
     */
    getTranslation: builder.mutation<
      FoodmaxapipbGetTranslationResponse,
      GetTranslationRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        menuGroupID,
        textType,
        entity,
        text,
        textSourceLang,
        textTargetLang,
        destinationKey,
      }) => ({
        url: menuGroupID
          ? endpoints.getTranslationMenuGroup
          : endpoints.getTranslation,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          textType,
          entity,
          text,
          textSourceLang,
          textTargetLang,
          destinationKey,
          ...(menuGroupID ? { menuGroupID } : {}),
        },
        timeout: 30000,
        extraOptions: { showError: false },
      }),
    }),
    /**
     * Verify translation before saving
     * POST /v1/verify-translation or /v1/menu-groups/verify-translation
     */
    getVerifyTranslation: builder.mutation<
      unknown,
      GetVerifyTranslationRequest
    >({
      query: ({ merchantID, merchantGroupID, menuGroupID, ...data }) => ({
        url: menuGroupID
          ? endpoints.getVerifyTranslationMenuGroup
          : endpoints.getVerifyTranslation,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          ...data,
          ...(menuGroupID ? { menuGroupID } : {}),
        },
        extraOptions: { showError: true },
      }),
    }),
    /**
     * Get single translation (reset translation for a single field)
     * POST /v1/menu-translations or /v1/menu-groups/menu-translations
     */
    getSingleTranslation: builder.mutation<
      FoodmaxapipbGetTranslationResponse,
      GetTranslationRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        menuGroupID,
        textType,
        entity,
        text,
        textSourceLang,
        textTargetLang,
        destinationKey,
      }) => ({
        url: menuGroupID
          ? endpoints.getTranslationMenuGroup
          : endpoints.getTranslation,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          textType,
          entity,
          text,
          textSourceLang,
          textTargetLang,
          destinationKey,
          ...(menuGroupID ? { menuGroupID } : {}),
        },
        timeout: 30000,
        extraOptions: { showError: false },
      }),
    }),
    /**
     * Fetch subdepartments (item-classes) for a Grab Category parent.
     * GET /v2/item-classes?parentID=... or /v1/menu-groups/item-classes?parentID=...&MenuGroupID=...
     */
    fetchSubdepartmentsForGrabCategory: builder.query<
      FoodmaxapipbListItemClassesResponse,
      FetchSubdepartmentsForGrabCategoryRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        parentID,
        locale = "en",
        menuGroupID,
      }) => ({
        url: menuGroupID
          ? endpoints.fetchSubdepartmentsForGrabCategoryMenuGroup
          : endpoints.fetchSubdepartmentsForGrabCategory,
        headers: {
          ...createRequestHeaders({ merchantID, merchantGroupID }),
          "Accept-Language": locale,
        },
        params: {
          parentID,
          ...(menuGroupID ? { MenuGroupID: menuGroupID } : {}),
        },
      }),
    }),
    /**
     * Reset a menu group back to its linked stores' menus.
     * PUT /v1/menu-groups/:menuGroupID/reset
     */
    resetMenuGroup: builder.mutation<void, ResetMenuGroupRequest>({
      query: ({ merchantGroupID, menuGroupID }) => ({
        url: `${endpoints.resetMenuGroup}/${menuGroupID}/reset`,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantGroupID }),
        data: { menuGroupID },
      }),
    }),
    /**
     * Fetch item-class (department) list for Grab Category tagging.
     * GET /v2/item-classes or /v1/menu-groups/item-classes (when menuGroupID is present)
     */
    fetchDepartmentsForGrabCategory: builder.query<
      FoodmaxapipbListItemClassesResponse,
      FetchDepartmentsForGrabCategoryRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        searchName = "",
        locale = "en",
        menuGroupID,
      }) => ({
        url: menuGroupID
          ? endpoints.fetchDepartmentsForGrabCategoryMenuGroup
          : endpoints.fetchDepartmentsForGrabCategory,
        headers: {
          ...createRequestHeaders({ merchantID, merchantGroupID }),
          "Accept-Language": locale,
        },
        params: {
          Name: searchName,
          ...(menuGroupID ? { MenuGroupID: menuGroupID } : {}),
        },
      }),
    }),
    /**
     * Sort items within a department (mart stores)
     * PUT /departments/items-sort  or  /v1/menu-groups/departments/items-sort
     */
    sortDepartmentItems: builder.mutation<void, SortDepartmentItemsRequest>({
      query: ({
        merchantID,
        merchantGroupID,
        departmentID,
        sorts,
        menuGroupID,
        showError = true,
        showSuccess = false,
        successOptions,
      }) => ({
        url: menuGroupID
          ? `/v1/menu-groups${endpoints.sortDepartmentItems}`
          : endpoints.sortDepartmentItems,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          departmentID,
          sorts,
          menuGroupID,
        },
        extraOptions: { showError, showSuccess, successOptions },
      }),
    }),
    /**
     * bulk delete items
     * DELETE /v2/items/bulk
     * itemID path param is "bulk"; selection scope is sent in the request body.
     */
    deleteItemV2: builder.mutation<void, DeleteItemV2Request>({
      query: ({
        merchantID,
        merchantGroupID,
        itemIDs,
        categoryIDs,
        departmentIDs,
        subDepartmentIDs,
        excludedSubDepartmentIDs,
        excludedItemIDs,
      }) => ({
        url: endpoints.deleteItemV2,
        method: HttpMethod.DELETE,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          itemIDs,
          categoryIDs,
          departmentIDs,
          subDepartmentIDs,
          excludedSubDepartmentIDs,
          excludedItemIDs,
        },
      }),
      invalidatesTags: PARTIAL_MENU_REFRESH_TAGS,
    }),
    /**
     * Update/create translation for menu entity
     * POST /v1/menu-translations (or /v1/menu-groups/menu-translations for menu groups)
     */
    updateExistingTranslation: builder.mutation<
      FetchTranslationResponse,
      UpdateExistingTranslationRequest
    >({
      query: ({ merchantID, merchantGroupID, menuGroupID, ...data }) => ({
        url: menuGroupID
          ? endpoints.updateExistingTranslationMenuGroup
          : endpoints.updateExistingTranslation,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          ...data,
          ...(menuGroupID && { menuGroupID }),
        },
        timeout: 30000,
        extraOptions: { showError: false },
      }),
    }),
    /**
     * Publish a menu group to linked stores
     * PUT /v1/menu-groups/:menuGroupID/publish
     */
    publishMenuGroup: builder.mutation<
      FoodmaxapipbPublishMenuGroupResponse,
      PublishMenuGroupRequest
    >({
      query: ({ merchantGroupID, menuGroupID, linkedStoreIDs }) => ({
        url: `${endpoints.publishMenuGroup}/${menuGroupID}/publish`,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantGroupID }),
        data: {
          linkedStoreIDs,
          menuGroupID,
        },
      }),
    }),
    /**
     * POST /v1/prediction-feedbacks or /v1/menu/prediction-feedbacks when menuGroupID present
     */
    postFeedbackGrabCategory: builder.mutation<
      void,
      PostFeedbackGrabCategoryRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        menuGroupID,
        predictionFeedback,
      }) => ({
        url: menuGroupID
          ? endpoints.postFeedbackGrabCategoryMenuGroup
          : endpoints.postFeedbackGrabCategory,
        method: HttpMethod.POST,
        headers: createRequestHeaders({
          merchantID: merchantID || menuGroupID,
          merchantGroupID,
        }),
        data: {
          PredictionFeedback: predictionFeedback,
        },
        extraOptions: {
          showError: true,
        },
      }),
    }),
    /**
     * Fetch supported attribute clusters for a given item class.
     * GET /v1/supported-attribute-clusters or /v2/menu-groups/supported-attribute-clusters
     */
    fetchSupportedAttributeCluster: builder.query<
      FetchSupportedAttributeClusterResponse,
      FetchSupportedAttributeClusterRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        isMenuGroup,
        menuGroupID,
        itemClassIDs,
        countryID,
        businessType,
        itemID,
      }) => ({
        url: isMenuGroup
          ? endpoints.fetchSupportedAttributeClusterMenuGroup
          : endpoints.fetchSupportedAttributeCluster,
        headers: createRequestHeaders({
          merchantID: merchantID || menuGroupID,
          merchantGroupID,
        }),
        params: {
          itemClassIDs,
          countryID,
          businessType,
          itemID,
        },
        extraOptions: { showError: false },
      }),
    }),
  }),
})

export const {
  useListSellingTimesQuery,
  useGetModifierGroupsByIDsQuery,
  useGetItemDetailsByItemIDsInfiniteQuery,
  useSearchItemsQuery,
  useLazySearchItemsQuery,
  useSearchMenusForMexQuery,
  useLazySearchMenusForMexQuery,
  useMartItemMenuDisplayV2Query,
  useUploadFileMutation,
  useLazyListAttributesQuery,
  useSortCategoriesMutation,
  useDeleteCategoryMutation,
  useDeleteMenuGroupMutation,
  useDeleteItemMutation,
  useDeleteItemV2Mutation,
  useUpdateItemsAvailableV1Mutation,
  useUpdateItemsSellingTimeMutation,
  useSortItemsMutation,
  useUpdateExistingTranslationMutation,
  useSortDepartmentsMutation,
  useFetchMenuGroupListQuery,
  useLazyFetchMenuGroupListQuery,
  useCreateMenuGroupMutation,
  useUpsertItemV2Mutation,
  useGetTranslationMutation,
  useGetVerifyTranslationMutation,
  useResetMenuGroupMutation,
  useFetchDepartmentsForGrabCategoryQuery,
  useLazyFetchDepartmentsForGrabCategoryQuery,
  useFetchSubdepartmentsForGrabCategoryQuery,
  useLazyFetchSubdepartmentsForGrabCategoryQuery,
  useGetSingleTranslationMutation,
  usePublishMenuGroupMutation,
  useSortDepartmentItemsMutation,
  usePostFeedbackGrabCategoryMutation,
  useFetchSupportedAttributeClusterQuery,
  useLazyFetchSupportedAttributeClusterQuery,
} = menuApi

/**
 * DelvPlatform API endpoints injected into dpApiBaseApi.
 * These previously required queryFn workarounds because menuApi used apiBaseUrl
 * as its baseQuery. Now they use the standard query pattern via dpApiBaseApi.
 */
export const menuDpApi = dpApiBaseApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetch count of items without photos across all stores in the merchant group.
     * GET /delvplatformapi/merchant/v1/merchant-group/store/merchants-items-without-photo-count
     */
    fetchItemsWithoutPhotoCount: builder.query<
      FetchItemsWithoutPhotoCountResponse,
      void
    >({
      query: () => ({
        url: dpApiEndpoints.fetchItemsWithoutPhotoCount,
      }),
    }),
    /**
     * Search outlets/stores with infinite (offset-based) pagination.
     * `offset` is managed as pageParam — do NOT include it in the query arg.
     * GET /delvplatformapi/merchant/v1/merchant-group/store/search
     */
    searchOutletsPaginated: builder.infiniteQuery<
      FetchStoreListResponse,
      Omit<SearchOutletsRequest, "offset">,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
          if (!lastPage.hasMore) return undefined
          return allPages.reduce(
            (sum, page) => sum + (page.merchants?.length || 0),
            0,
          )
        },
      },
      query: ({ queryArg, pageParam }) => {
        const { merchantID, merchantGroupID, ...params } = queryArg
        return {
          url: dpApiEndpoints.searchOutlets,
          headers: createRequestHeaders({ merchantID, merchantGroupID }),
          params: {
            ...params,
            offset: pageParam,
            includeInactive: true,
            cityIDs: params.city,
            businessTypes: params.businessType,
            deliverOptionsOriginal: params.deliverOption,
          },
        }
      },
    }),
    /**
     * Fetch merchant aggregate (cities, business types, deliver options)
     * GET /v1/merchant-group/store/aggregate-by-fields
     */
    fetchMerchantAggregate: builder.query<
      FetchMerchantAggregateResponse,
      FetchMerchantAggregateRequest
    >({
      query: ({ merchantGroupID, fields }) => ({
        url: dpApiEndpoints.fetchMerchantAggregate,
        headers: createRequestHeaders({ merchantGroupID }),
        params: {
          fields,
          modelType: "integrated",
          includeInactive: true,
        },
      }),
    }),
  }),
})

export const {
  useSearchOutletsPaginatedInfiniteQuery,
  useFetchItemsWithoutPhotoCountQuery,
  useFetchMerchantAggregateQuery,
} = menuDpApi

/**
 * Portal (FoodTroy) API endpoints injected into portalBaseApi.
 */
export const menuPortalApi = portalBaseApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetch merchant detail (v2) via the portal foodtroy endpoint.
     * GET /foodtroy/v2/{countryCode}/merchants/{merchantID}
     */
    fetchMerchantDetail: builder.query<
      FetchMerchantDetailResponse,
      FetchMerchantDetailRequest
    >({
      query: ({ merchantID, countryCode }) => ({
        url: `/v2/${countryCode}/merchants/${merchantID}`,
        headers: createRequestHeaders({ merchantID }),
        extraOptions: { showGlobalLoading: true },
      }),
    }),
  }),
})

export const {
  useFetchMerchantDetailQuery,
  useLazyFetchMerchantDetailQuery,
} = menuPortalApi

export function flattenInfiniteItemsByIDs(
  data:
    | InfiniteData<FoodmaxapipbGetItemDetailsByItemIDsResponse, number>
    | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.items || [])
}

export function flattenInfiniteOutlets(
  data: InfiniteData<FetchStoreListResponse, number> | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.merchants || [])
}
